import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Product from '../Product/Product';
import styles from './ProductList.module.css';

/**
 * ProductList - Displays a filterable, searchable list of products
 * 
 * @param {string} seriesId - Series ID to fetch products from
 * @param {string} thumbSize - Thumbnail size (default: "256")
 * @param {Object} filters - Filter rules in format { key: value } to match exact values
 * @param {string} strictFilters - Expression-based filters (e.g., "categoryName=='LVT' && warranty=='6X36'")
 * @param {string} searchValue - Search term to match across multiple columns
 * @param {Array} searchColumns - Array of column names to search in (default: ['name', 'color', 'description'])
 * @param {Function} onProductSelect - Callback when product is selected
 * @param {string} titleAttribute - Product attribute to use as title (default: 'color')
 * @param {string} descriptionAttribute - Product attribute to use as description (default: 'name')
 * @param {Function} transformTitle - Optional function to transform title text before display
 * @param {Function} transformDescription - Optional function to transform description text before display
 * @param {string} sortBy - Product attribute to sort by (optional)
 * @param {string} sortOrder - Sort order: 'asc' or 'desc' (default: 'asc')
 * @param {string} className - Additional CSS class
 */
export default function ProductList({ 
    seriesId, 
    thumbSize = "256",
    filters = {}, 
    strictFilters = "",
    searchValue = "",
    searchColumns = ['name', 'color', 'description', 'manufacturerSeriesName'],
    onProductSelect,
    titleAttribute = 'color',
    descriptionAttribute = 'name',
    transformTitle,
    transformDescription,
    sortBy = null,
    sortOrder = 'asc',
    className = "",
    gridClassName = "",
    selectedProductId: externalSelectedId = null
}) {
    const [productData, setProductData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [internalSelectedId, setInternalSelectedId] = useState(null);
    
    // Use external selectedProductId if provided, otherwise use internal state
    const selectedProductId = externalSelectedId !== null ? externalSelectedId : internalSelectedId;

    // Fetch product data
    useEffect(() => {
        if (!seriesId) return;

        setLoading(true);
        setError(null);

        const URL = `https://lizard.chameleonpower.com/CDN/ProductsBySeries/${seriesId}.json`;
        
        fetch(URL)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`Failed to fetch products: ${response.status}`);
                }
                return response.json();
            })
            .then((data) => {
                setProductData(Array.isArray(data) ? data : []);
                setLoading(false);
            })
            .catch((error) => {
                console.error('Error fetching products:', error);
                setError(error.message);
                setLoading(false);
            });
    }, [seriesId]);

    // Apply filters
    const applyFilters = (products) => {
        if (!filters || Object.keys(filters).length === 0) {
            return products;
        }

        return products.filter(product => {
            // Check if product matches all filter rules
            return Object.entries(filters).every(([key, value]) => {
                const productValue = product[key];
                
                // Handle null/undefined
                if (productValue === null || productValue === undefined) {
                    return false;
                }
                
                // Convert to string for comparison
                return String(productValue).toLowerCase() === String(value).toLowerCase();
            });
        });
    };

    // Apply strict filters (expression-based like "categoryName=='LVT' && warranty=='6X36'")
    const applyStrictFilters = (products) => {
        if (!strictFilters || strictFilters.trim() === '') {
            return products;
        }

        return products.filter(product => {
            try {
                // Parse the expression and evaluate it against the product
                let expression = strictFilters;
                
                // Replace field references with actual product values
                // Match patterns like: fieldName=='value' or fieldName!='value'
                const fieldPattern = /(\w+)\s*(==|!=)\s*['"]([^'"]*)['"]/g;
                
                expression = expression.replace(fieldPattern, (match, field, operator, value) => {
                    const productValue = product[field];
                    const productValueStr = productValue !== null && productValue !== undefined 
                        ? String(productValue) 
                        : '';
                    
                    if (operator === '==') {
                        return productValueStr.toLowerCase() === value.toLowerCase() ? 'true' : 'false';
                    } else if (operator === '!=') {
                        return productValueStr.toLowerCase() !== value.toLowerCase() ? 'true' : 'false';
                    }
                    return 'false';
                });

                // Evaluate the boolean expression (only allows true, false, &&, ||, parentheses)
                // Replace && and || with JavaScript equivalents for safe eval
                expression = expression.replace(/&&/g, ' && ').replace(/\|\|/g, ' || ');
                
                // Only evaluate if expression contains only safe characters
                if (/^[\s()&|truefalse]+$/i.test(expression)) {
                    // eslint-disable-next-line no-eval
                    return eval(expression);
                }
                
                return false;
            } catch (e) {
                console.error('Error evaluating strictFilters:', e);
                return false;
            }
        });
    };

    // Apply search
    const applySearch = (products) => {
        if (!searchValue || searchValue.trim() === '') {
            return products;
        }

        const searchLower = searchValue.toLowerCase().trim();

        return products.filter(product => {
            // Check if search term matches any of the specified columns (partial match)
            return searchColumns.some(column => {
                const columnValue = product[column];
                
                // Handle null/undefined
                if (columnValue === null || columnValue === undefined) {
                    return false;
                }
                
                // Convert to string and check for partial match
                return String(columnValue).toLowerCase().includes(searchLower);
            });
        });
    };

    // Apply sorting
    const applySort = (products) => {
        if (!sortBy) return products;

        return [...products].sort((a, b) => {
            const aVal = a[sortBy] || '';
            const bVal = b[sortBy] || '';
            
            const comparison = String(aVal).localeCompare(String(bVal), undefined, { sensitivity: 'base' });
            return sortOrder === 'desc' ? -comparison : comparison;
        });
    };

    // Get filtered and searched products
    const getFilteredProducts = () => {
        let filtered = productData;
        filtered = applyFilters(filtered);
        filtered = applyStrictFilters(filtered);
        filtered = applySearch(filtered);
        filtered = applySort(filtered);
        return filtered;
    };

    const filteredProducts = getFilteredProducts();

    // Handle product selection
    const handleProductSelect = (id) => {
        const selectedProduct = productData.find(product => product.id === id);
        if (selectedProduct && onProductSelect) {
            setInternalSelectedId(id);
            onProductSelect(selectedProduct);
        }
    };

    // Render loading state
    if (loading) {
        return (
            <div className={`${styles.productList} ${className}`}>
                <div className={styles.message}>Loading products...</div>
            </div>
        );
    }

    // Render error state
    if (error) {
        return (
            <div className={`${styles.productList} ${className}`}>
                <div className={styles.errorMessage}>Error: {error}</div>
            </div>
        );
    }

    // Render product list
    return (
        <div className={`${styles.productList} ${className}`} data-series={seriesId}>
            {filteredProducts.length > 0 ? (
                <ul className={`${styles.productGrid} ${gridClassName}`}>
                    {filteredProducts.map(product => {
                        const title = product[titleAttribute] || '';
                        const description = product[descriptionAttribute] || '';
                        
                        return (
                            <Product
                                key={product.id}
                                id={product.id}
                                product={product}
                                size={thumbSize}
                                title={transformTitle ? transformTitle(title) : title}
                                description={transformDescription ? transformDescription(description) : description}
                                onSelect={handleProductSelect}
                                isActive={product.id === selectedProductId}
                            />
                        );
                    })}
                </ul>
            ) : (
                <div className={styles.emptyMessage}>
                    {searchValue || Object.keys(filters).length > 0 
                        ? 'No products match your criteria.' 
                        : 'No products available.'}
                </div>
            )}
        </div>
    );
}

ProductList.propTypes = {
    seriesId: PropTypes.string.isRequired,
    thumbSize: PropTypes.string,
    filters: PropTypes.object,
    strictFilters: PropTypes.string,
    searchValue: PropTypes.string,
    searchColumns: PropTypes.arrayOf(PropTypes.string),
    onProductSelect: PropTypes.func,
    titleAttribute: PropTypes.string,
    descriptionAttribute: PropTypes.string,
    transformTitle: PropTypes.func,
    transformDescription: PropTypes.func,
    sortBy: PropTypes.string,
    sortOrder: PropTypes.oneOf(['asc', 'desc']),
    className: PropTypes.string,
    gridClassName: PropTypes.string,
    selectedProductId: PropTypes.number,
};